import { TOKEN_KEY, MFA_SESSION_KEY } from "./constants";
import type { AuthProviderToken, MfaSession } from "./types";

export interface TokenStorage {
  getToken(): AuthProviderToken | null;
  setToken(token: AuthProviderToken): void;
  clearToken(): void;
  getMfa(): MfaSession | null;
  setMfa(session: MfaSession): void;
  clearMfa(): void;
  /**
   * Optional aggressive wipe used on logout. Defaults to clearToken + clearMfa
   * when not implemented. LocalStorageTokenStore overrides this to wipe ALL
   * localStorage + sessionStorage (preserves admin's existing logout behavior),
   * except any keys matching `preserveKeyPrefixes`, see clearAll() below.
   */
  clearAll?(): void;
}

export interface LocalStorageTokenStoreOptions {
  tokenKey?: string;
  mfaKey?: string;
  /**
   * When true, clearAll() wipes ALL localStorage and sessionStorage.
   * When false, clearAll() removes only the auth-related keys.
   * Default: true (matches admin's pre-extraction behavior).
   */
  aggressiveClearAll?: boolean;
  /**
   * localStorage key prefixes to keep when aggressiveClearAll wipes
   * everything else — for app-level flags (e.g. "seen this nudge before")
   * that are meant to survive logout/login, not just the current session.
   */
  preserveKeyPrefixes?: string[];
}

export class LocalStorageTokenStore implements TokenStorage {
  private readonly tokenKey: string;
  private readonly mfaKey: string;
  private readonly aggressiveClearAll: boolean;
  private readonly preserveKeyPrefixes: string[];

  constructor(opts: LocalStorageTokenStoreOptions = {}) {
    this.tokenKey = opts.tokenKey ?? TOKEN_KEY;
    this.mfaKey = opts.mfaKey ?? MFA_SESSION_KEY;
    this.aggressiveClearAll = opts.aggressiveClearAll ?? true;
    this.preserveKeyPrefixes = opts.preserveKeyPrefixes ?? [];
  }

  getToken(): AuthProviderToken | null {
    try {
      const raw = localStorage.getItem(this.tokenKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as AuthProviderToken | Record<string, never>;
      if (!parsed || Object.keys(parsed).length === 0) return null;
      return parsed as AuthProviderToken;
    } catch {
      return null;
    }
  }

  setToken(token: AuthProviderToken): void {
    localStorage.setItem(this.tokenKey, JSON.stringify(token));
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  getMfa(): MfaSession | null {
    try {
      const raw = localStorage.getItem(this.mfaKey);
      return raw ? (JSON.parse(raw) as MfaSession) : null;
    } catch {
      return null;
    }
  }

  setMfa(session: MfaSession): void {
    localStorage.setItem(this.mfaKey, JSON.stringify(session));
  }

  clearMfa(): void {
    localStorage.removeItem(this.mfaKey);
  }

  clearAll(): void {
    if (this.aggressiveClearAll) {
      const preserved = this.preserveKeyPrefixes.length
        ? this.readPreservedEntries()
        : [];
      localStorage.clear();
      sessionStorage.clear();
      preserved.forEach(([key, value]) => localStorage.setItem(key, value));
    } else {
      this.clearToken();
      this.clearMfa();
    }
  }

  private readPreservedEntries(): [string, string][] {
    const entries: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (this.preserveKeyPrefixes.some((prefix) => key.startsWith(prefix))) {
        const value = localStorage.getItem(key);
        if (value !== null) entries.push([key, value]);
      }
    }
    return entries;
  }
}
